import {FC, useState} from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import {useIntl} from 'react-intl'
import {BranchModel} from '../models/BranchModel'
import {BranchStatus} from '../constants'

type Props = {
  className?: string
  onClose: () => void
  onConfirm: (transferToBranchId: number) => void
  isShow: boolean
  currentBranch: BranchModel | null
  availableBranches: BranchModel[]
}

const DeactivateBranchModal: FC<Props> = (props) => {
  const intl = useIntl()
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null)

  const onClose = () => {
    setSelectedBranchId(null)
    props.onClose()
  }

  const onConfirm = () => {
    if (selectedBranchId) {
      props.onConfirm(selectedBranchId)
      setSelectedBranchId(null)
    }
  }

  const handleBranchSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const branchId = parseInt(event.target.value)
    setSelectedBranchId(branchId || null)
  }

  // Filter out the current branch and inactive branches from available options
  const activeBranches = props.availableBranches.filter(
    (branch) => 
      branch.id !== props.currentBranch?.id && 
      branch.status === BranchStatus.ACTIVE
  )

  return (
    <Modal
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
      show={props.isShow}
      onHide={onClose}
      backdrop='static'
    >
      <Modal.Header closeButton>
        <p className='card-label fw-bolder fs-1 mb-1'>
          {intl.formatMessage({id: 'BRANCH.DEACTIVATE.TITLE'})}
        </p>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-4'>
          <p>{intl.formatMessage({id: 'BRANCH.DEACTIVATE.MESSAGE'})}</p>
          {props.currentBranch && (
            <p className='text-muted'>
              <strong>{intl.formatMessage({id: 'BRANCH.NAME'})}:</strong> {props.currentBranch.name}
            </p>
          )}
        </div>
        
        <div className='mb-4'>
          <Form.Label className='required fw-bold'>
            {intl.formatMessage({id: 'BRANCH.DEACTIVATE.SELECT_BRANCH'})}
          </Form.Label>
          <Form.Select
            value={selectedBranchId || ''}
            onChange={handleBranchSelect}
            required
          >
            <option value=''>
              {intl.formatMessage({id: 'BRANCH.DEACTIVATE.SELECT_PLACEHOLDER'})}
            </option>
            {activeBranches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name} - {branch.address}
              </option>
            ))}
          </Form.Select>
        </div>

        {activeBranches.length === 0 && (
          <div className='alert alert-warning'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            {intl.formatMessage({id: 'BRANCH.DEACTIVATE.NO_BRANCHES_WARNING'})}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end flex-row-fluid'>
          <Button
            type='button'
            variant='secondary'
            onClick={onClose}
            className='btn btn-light me-3'
          >
            {intl.formatMessage({id: 'BRANCH.DEACTIVATE.CANCEL'})}
          </Button>

          <Button 
            type='button' 
            variant='danger' 
            onClick={onConfirm}
            disabled={!selectedBranchId || activeBranches.length === 0}
          >
            {intl.formatMessage({id: 'BRANCH.DEACTIVATE.CONFIRM'})}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default DeactivateBranchModal
